import {
  createContext, useContext, useEffect, useRef, useState, type CSSProperties, type ReactNode, type Ref,
} from 'react';
import { Icon } from '@/components/kit';
import { useNarrowViewport } from '@/components/kit/useNarrow';
import type { FlowRole } from '../shared/useFlows';

/**
 * The few pieces every Library section needs, in one place — a role's label
 * and chip colour, the text-field look the editors share, a select that wears
 * the board's "▾" rather than a browser control, and the board's
 * value-then-pencil editing.
 *
 * These live in the lane, not in the kit: the kit is shared code and a new
 * component there would collide with every other role being ported tonight.
 */

/** Desk-only explanatory copy: the desk board carries these footnotes, the phone board does not. */
export function DeskOnly({ children }: { children: ReactNode }) {
  const narrow = useNarrowViewport();
  return narrow ? null : <>{children}</>;
}

export const ROLE_LABEL: Record<FlowRole, string> = {
  nurse: 'Nurse',
  technician: 'Technician',
  reception: 'Reception',
  doctor: 'You',
};

/**
 * ONE EDITOR, TWO ENTRANCES, TWO VOICES. The Library is the doctor's, so a
 * doctor step reads "You" and a chain reads "nurse → technician → you". The
 * clinic manager reaches the same editor from Settings ▸ Flows (`g_flows`),
 * where nobody is "you" — the board writes "nurse → technician → doctor". The
 * page that mounts the editor says whose screen it is; the editor never has
 * to know.
 */
const CLINIC_ROLE_LABEL: Record<FlowRole, string> = { ...ROLE_LABEL, doctor: 'Doctor' };
const FlowVoice = createContext<Record<FlowRole, string>>(ROLE_LABEL);

export function FlowVoiceProvider({ clinic, children }: { clinic?: boolean; children: ReactNode }) {
  return <FlowVoice.Provider value={clinic ? CLINIC_ROLE_LABEL : ROLE_LABEL}>{children}</FlowVoice.Provider>;
}

/** The role labels for whoever is reading — "You" in the Library, "Doctor" in Settings. */
export function useRoleLabel(): Record<FlowRole, string> {
  return useContext(FlowVoice);
}

/** True under Settings ▸ Flows, where the READY lane is "the doctor's", not "your". */
export function useIsClinicVoice(): boolean {
  return useContext(FlowVoice) === CLINIC_ROLE_LABEL;
}

export const ROLE_CHIP: Record<FlowRole, 'success' | 'info' | 'warn' | 'soft'> = {
  nurse: 'success',
  technician: 'info',
  reception: 'warn',
  doctor: 'soft',
};

export const box: CSSProperties = {
  background: 'var(--surface-0)',
  color: 'var(--ink-900)',
  border: '1px solid var(--stroke-200)',
  borderRadius: 'var(--r-sm)',
  padding: '6px 8px',
  font: 'inherit',
};

/**
 * A SELECT THAT LOOKS LIKE THE BOARD'S "▾". §13.3's rule is that anything
 * wearing a chevron must open a real picker — so this IS a native `<select>`
 * (a real picker, keyboard-reachable, screen-reader correct), but it is laid
 * OVER the board's own text rather than replacing it.
 *
 * WHY THE OVERLAY. A `<select>` measures itself against its widest OPTION, so
 * the chevron parked at the card's right edge instead of beside the value —
 * and in NEEDS, whose options are item names, it was pushed out of the card
 * altogether and the control read as inert text. Now the visible label is the
 * board's own type with the board's "▾" straight after the value, and the
 * select sits invisibly on top of it: the box is the size of the VALUE, and
 * the picker is still the browser's.
 */
export function InlineSelect({
  value, onChange, options, className, 'aria-label': label, chevron = true,
}: {
  value: string;
  onChange: (v: string) => void;
  options: Array<{ value: string; label: string }>;
  /** Kit classes for the VISIBLE label — the board's own type. */
  className?: string;
  'aria-label': string;
  chevron?: boolean;
}) {
  const shown = options.find((o) => o.value === value)?.label ?? value;
  return (
    <span className="dr-sel">
      <span className={className} aria-hidden="true">
        {shown}{chevron ? ' ▾' : null}
      </span>
      <select
        className="dr-selnative"
        value={value}
        aria-label={label}
        onChange={(e) => onChange(e.target.value)}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </span>
  );
}

/**
 * A CHIP THE BOARD DRAWS AT 24px THAT IS STILL A CONTROL.
 *
 * The kit's `Chip` takes `onClick` and becomes `.chip-act` — 40px tall, with a
 * 44px hit box behind it. That is the phone's rule, and inside these panes it
 * is wrong twice over: the desk is pointer-first (DChip says so itself), and
 * the board draws every one of these as a 24px `chip chip-sm`, so a 40px chip
 * pushed the whole pane 16px down. This keeps the board's size and buys the
 * target back with a taller invisible ::before, exactly as `.dchip` does.
 */
export function ChipButton({
  children, kind = 'outline', onClick, label, className, pressed,
}: {
  children: ReactNode;
  kind?: 'outline' | 'soft' | 'success' | 'warn' | 'info' | 'pink' | 'brand' | 'danger';
  onClick: () => void;
  label?: string;
  className?: string;
  pressed?: boolean;
}) {
  return (
    <button type="button" className={`chip chip-${kind} chip-sm dr-chipbtn${className ? ` ${className}` : ''}`}
      onClick={onClick} aria-label={label} aria-pressed={pressed}>
      {children}
    </button>
  );
}

/**
 * THE BOARD EDITS A VALUE, NOT A FORM. Every editor on these boards shows what
 * is written — "Corneal topography", "20 min", the sheet itself — with a small
 * mark beside it, and becomes a field only when asked. A pane of bordered
 * inputs is a different screen: taller, louder, and it hides the content
 * behind the chrome for typing it.
 *
 * So the pattern lives here once: the value as text in the board's own
 * classes, the board's mark, and a real field on click or Enter. Blur and
 * Enter commit; Escape puts the old value back.
 */
export function InlineEdit({
  value, onCommit, label, display, className, placeholder, multiline = false,
  mark = 'icon', numeric = false, suffix, fieldStyle, rows = 4, startEditing = false,
}: {
  value: string;
  onCommit: (v: string) => void;
  /** Accessible name of the field this opens. */
  label: string;
  /** What to draw instead of the raw value — the board's quotes, "20 min"… */
  display?: ReactNode;
  className?: string;
  placeholder?: string;
  multiline?: boolean;
  /** The board draws a brand pencil icon, a "✎" glyph, or nothing. */
  mark?: 'icon' | 'glyph' | 'none';
  numeric?: boolean;
  suffix?: ReactNode;
  fieldStyle?: CSSProperties;
  rows?: number;
  /**
   * Open as the field, not as the value. For a value that does not exist yet
   * (a new flow's name): "click the empty line to type" is a door with no
   * handle, and the card looks filled when it is not.
   */
  startEditing?: boolean;
}) {
  const [editing, setEditing] = useState(startEditing);
  const [draft, setDraft] = useState(value);
  const ref = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);

  useEffect(() => { if (!editing) setDraft(value); }, [value, editing]);
  useEffect(() => { if (editing) ref.current?.focus(); }, [editing]);

  const commit = () => {
    if (draft !== value) onCommit(draft);
    /* A FIELD THAT OPENED BECAUSE THERE IS NO VALUE STAYS A FIELD while it is
       still empty. The kit's Modal moves focus into the dialog after it
       mounts, which blurs this input — closing on that blur left the card
       looking like a filled value line with nothing in it. */
    if (startEditing && !draft.trim()) return;
    setEditing(false);
  };
  const cancel = () => { setDraft(value); setEditing(false); };

  if (editing) {
    const style = { ...box, width: '100%', ...fieldStyle };
    return multiline ? (
      <textarea value={draft} aria-label={label} placeholder={placeholder} rows={rows}
        style={style} ref={ref as Ref<HTMLTextAreaElement>} onBlur={commit}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Escape') cancel(); }} />
    ) : (
      /* A WHOLE POUND, TYPED — `type="number"` draws the browser's spinner,
         which the board does not, and lets "4e5" into a price. */
      <input value={draft} aria-label={label} placeholder={placeholder}
        type="text" inputMode={numeric ? 'numeric' : undefined}
        style={style} ref={ref as Ref<HTMLInputElement>}
        onBlur={commit}
        onChange={(e) => setDraft(numeric ? e.target.value.replace(/[^\d]/g, '') : e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); commit(); }
          if (e.key === 'Escape') cancel();
        }} />
    );
  }

  return (
    <span className={`dr-inlineedit${className ? ` ${className}` : ''}`} role="button" tabIndex={0}
      aria-label={`${label} — ${value || 'not set'}. Edit`}
      onClick={() => setEditing(true)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setEditing(true); }
      }}>
      {display ?? (value || <span className="mut">{placeholder ?? 'not set'}</span>)}
      {suffix}
      {mark === 'icon' ? <span className="brand dr-mark"><Icon name="pencil" size="sm" /></span> : null}
      {/* The glyph carries its own space; a margin on top of it wrapped the
          board's "20 min ✎" onto a second line in a 66px card. */}
      {mark === 'glyph' ? ' ✎' : null}
    </span>
  );
}

/* ---------- the phone's list rows (g_l2 g_l3 g_l5 g_l8 g_l12 g_l14) ---------- */

export interface PhoneRow {
  id: string;
  title: ReactNode;
  /** The right of the title line — a chip, a pencil, both. */
  trailing?: ReactNode;
  /** Everything under the title: a caption, a row of chips. */
  lines?: ReactNode;
  selected?: boolean;
  /** An amber outline — a draft. */
  warn?: boolean;
  onOpen: () => void;
  /** Swipe the row left to reveal Delete (the editor keeps its own Delete too). */
  onDelete?: () => void;
}

const SWIPE_W = 104;

/** One card: title + trailing on a line, `lines` under it; a left swipe opens its Delete. */
function PhoneRowCard({ row }: { row: PhoneRow }) {
  const [dx, setDx] = useState(0);
  const start = useRef<{ x: number; y: number; base: number; lock: boolean } | null>(null);
  const open = dx <= -SWIPE_W / 2;
  const onStart = (e: React.TouchEvent) => {
    if (!row.onDelete) return;
    start.current = { x: e.touches[0].clientX, y: e.touches[0].clientY, base: dx, lock: false };
  };
  const onMove = (e: React.TouchEvent) => {
    const s = start.current;
    if (!s) return;
    const mx = e.touches[0].clientX - s.x;
    if (!s.lock && Math.abs(e.touches[0].clientY - s.y) > Math.abs(mx)) { start.current = null; return; }
    s.lock = true;
    setDx(Math.max(-SWIPE_W, Math.min(0, s.base + mx)));
  };
  const onEnd = () => {
    if (!start.current) return;
    start.current = null;
    setDx((v) => (v <= -SWIPE_W / 2 ? -SWIPE_W : 0));
  };
  return (
    <div style={{ position: 'relative', marginBottom: 8, borderRadius: 'var(--r-md)', overflow: 'hidden' }}>
      {row.onDelete ? (
        <button type="button" tabIndex={open ? 0 : -1} aria-label="Delete"
          onClick={() => { setDx(0); row.onDelete?.(); }}
          style={{
            position: 'absolute', insetBlock: 0, insetInlineEnd: 0, width: SWIPE_W, border: 0,
            background: 'var(--danger-100)', color: 'var(--danger-600)', font: 'inherit', fontWeight: 600,
          }}>
          Delete
        </button>
      ) : null}
      <div
        role="button" tabIndex={0}
        onClick={() => (open ? setDx(0) : row.onOpen())}
        onKeyDown={(e) => { if (e.key === 'Enter') row.onOpen(); }}
        onTouchStart={onStart} onTouchMove={onMove} onTouchEnd={onEnd}
        className="card card-btn"
        style={{
          position: 'relative', margin: 0, cursor: 'pointer', transform: `translateX(${dx}px)`,
          transition: start.current ? 'none' : 'transform .18s ease',
          ...(row.warn ? { borderColor: 'var(--beige-500)' } : row.selected ? { borderColor: 'var(--brand-300)' } : {}),
        }}
      >
        <div className="flex" style={{ gap: 8, alignItems: 'center' }}>
          <p className="t-strong grow" style={{ minWidth: 0 }}>{row.title}</p>
          {row.trailing ? <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>{row.trailing}</span> : null}
        </div>
        {row.lines ? <div className="t-sec mut mt4">{row.lines}</div> : null}
      </div>
    </div>
  );
}

export function PhoneRowList({ rows, empty, foot }: { rows: PhoneRow[]; empty?: ReactNode; foot?: ReactNode }) {
  return (
    <div>
      {rows.length ? rows.map((r) => <PhoneRowCard key={r.id} row={r} />) : empty}
      {foot}
    </div>
  );
}

/** The footnote under a phone list — one short line, as the board writes it. */
export function PhoneFootnote({ children }: { children: ReactNode }) {
  return <p className="t-sec mut" style={{ padding: '8px 4px 0' }}>{children}</p>;
}
